/**
 * SOAP order cache
 *
 * The SOAP note view needs the note's linked order (GET /orderget/:id, ~46 KB) and
 * whether the patient already has a paid initial appointment (GET
 * /check-initial-appointment). It used to keep both only in component state, so every
 * switch back to a note (Summary -> note) asked for them again. They are kept here,
 * per order id and per paid-initial question, and dropped whenever they may be out of
 * date (see store/soapOrderCache.js for the rules and the live-update wiring).
 *
 *   ordersById       { [orderId]: { order, cachedAt } }   order exactly as /orderget returns it
 *   paidInitialByKey { [key]: { value, cachedAt } }        key = paidInitialKey(...)
 *                    (each holds at most its MAX_CACHED_* newest entries)
 *   orderEpoch, paidEpoch  count every drop of orders / paid-initial answers; an answer
 *                    asked for before a drop may predate the change and is not stored
 */

import { createSlice } from '@reduxjs/toolkit';

// Matched by type so this slice doesn't import the thunks, which import its actions.
const FETCH_SOAP_NOTE_FULFILLED = 'emr/fetchSoapNote/fulfilled';
const PATCH_ENCOUNTER = 'emr/patchEncounter';

// Enough for every note a doctor keeps open; the oldest entries go first.
export const MAX_CACHED_SOAP_ORDERS = 30;
export const MAX_CACHED_SOAP_PAID_INITIAL = 30;

/** Key for one paid-initial question, built from exactly what the request sends. */
export const paidInitialKey = (patientId, currentOrderId, currentDate) => (
  [patientId, currentOrderId, currentDate].map((part) => String(part ?? '')).join('|')
);

/** UTC calendar day of an order, as the server names days in its live-update events. */
const orderDay = (order) => {
  const value = order?.date;
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
};

const initialState = {
  ordersById: {},
  paidInitialByKey: {},
  orderEpoch: 0,
  paidEpoch: 0,
};

const dropPaidInitial = (state) => {
  state.paidInitialByKey = {};
  state.paidEpoch += 1;
};

// Drops the oldest entries of a { [key]: { cachedAt, ... } } map beyond `max`.
const keepNewest = (entries, max) => {
  const keys = Object.keys(entries);
  if (keys.length <= max) return;
  keys
    .sort((a, b) => entries[a].cachedAt - entries[b].cachedAt)
    .slice(0, keys.length - max)
    .forEach((key) => { delete entries[key]; });
};

const storeOrder = (state, order, cachedAt) => {
  if (!order?._id) return;
  state.ordersById[String(order._id)] = { order, cachedAt };
  keepNewest(state.ordersById, MAX_CACHED_SOAP_ORDERS);
};

const storePaidInitial = (state, key, value, cachedAt) => {
  state.paidInitialByKey[key] = { value: Boolean(value), cachedAt };
  keepNewest(state.paidInitialByKey, MAX_CACHED_SOAP_PAID_INITIAL);
};

const soapOrderCacheSlice = createSlice({
  name: 'soapOrderCache',
  initialState,
  reducers: {
    // A full order as /orderget returns it. The caller passes a copy it won't mutate.
    cacheSoapOrder: {
      reducer: (state, action) => {
        storeOrder(state, action.payload.order, action.payload.cachedAt);
      },
      prepare: (order) => ({ payload: { order, cachedAt: Date.now() } }),
    },

    // The order changed (or may have): forget it, and every paid-initial answer,
    // since those depend on the patient's other orders.
    invalidateSoapOrder: (state, action) => {
      if (action.payload) delete state.ordersById[String(action.payload)];
      state.orderEpoch += 1;
      dropPaidInitial(state);
    },

    cacheSoapPaidInitial: {
      reducer: (state, action) => {
        const { key, value, cachedAt } = action.payload;
        storePaidInitial(state, key, value, cachedAt);
      },
      prepare: ({ key, value }) => ({ payload: { key, value, cachedAt: Date.now() } }),
    },

    clearSoapOrderCache: (state) => {
      state.ordersById = {};
      state.orderEpoch += 1;
      dropPaidInitial(state);
    },

    // A live-update event from the server. 'ordered' means orders were written: the
    // ones on the named days (all of them when no days are named) are dropped.
    // Paid-initial answers depend on any of the patient's orders and on Shopify
    // payments, so either kind of event drops them all.
    applySoapOrderLiveRefresh: (state, action) => {
      const { sources = [], dates = null } = action.payload || {};
      if (sources.includes('ordered')) {
        state.orderEpoch += 1;
        if (Array.isArray(dates) && dates.length > 0) {
          const days = new Set(dates);
          Object.keys(state.ordersById).forEach((id) => {
            const day = orderDay(state.ordersById[id].order);
            if (!day || days.has(day)) delete state.ordersById[id];
          });
        } else {
          state.ordersById = {};
        }
      }
      if (sources.includes('ordered') || sources.includes('shopify')) {
        dropPaidInitial(state);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // GET /get-soap-note/:id?include=order brings the order and its paid-initial answer
      .addMatcher((action) => action.type === FETCH_SOAP_NOTE_FULFILLED, (state, action) => {
        const { data, receivedAt = Date.now(), orderCacheable = true, paidCacheable = true } = action.payload || {};
        if (orderCacheable && data?.order?._id) storeOrder(state, data.order, receivedAt);
        const paid = data?.paidInitialAppointment;
        if (paidCacheable && paid && typeof paid.hasPaidInitialAppointment === 'boolean') {
          storePaidInitial(
            state,
            paidInitialKey(paid.patientId, paid.currentOrderId, paid.currentDate),
            paid.hasPaidInitialAppointment,
            receivedAt,
          );
        }
      })
      // An encounter refreshed elsewhere (e.g. after the LPDS popup on the encounters
      // list): the order was edited there.
      .addMatcher(
        (action) => action.type === PATCH_ENCOUNTER,
        (state, action) => {
          const id = action.payload?.encounter?._id;
          if (id) delete state.ordersById[String(id)];
          state.orderEpoch += 1;
          dropPaidInitial(state);
        },
      );
  },
});

export const {
  cacheSoapOrder,
  invalidateSoapOrder,
  cacheSoapPaidInitial,
  clearSoapOrderCache,
  applySoapOrderLiveRefresh,
} = soapOrderCacheSlice.actions;

export default soapOrderCacheSlice.reducer;
